import { Check } from '@mui/icons-material';
import {
  Backdrop,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  styled,
  Typography,
  type SelectProps,
  type TextFieldProps,
} from '@mui/material';
import { isArray } from 'lodash';
import { forwardRef, Ref, useId } from 'react';
import { Column } from '../Layout';

const InvisibleBackdrop = styled(Backdrop)({
  backgroundColor: 'transparent',
});

const StyledSelect = styled(MuiSelect)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: theme.spacing(theme.spacingSizes.xs),
  },
}));

const Option = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(theme.spacingSizes.xs),
}));

const Placeholder = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: theme.fontSizes.regular,
}));

type BasicSelectBaseProps<
  T extends string | number | Array<string | number> | null = string,
> = {
  value: T | undefined;
  onChange: (value: T) => void;
  onClose?: () => void;
  label?: string;
  id?: string;
  options: Array<{
    value: T extends Array<infer U> ? U : T;
    label: string;
    description?: string;
    disabled?: boolean;
  }>;
  noOptionsMessage?: string;
  className?: string;
  nullOptionLabel?: string;
} & Pick<
  SelectProps<T>,
  'fullWidth' | 'error' | 'size' | 'multiple' | 'renderValue'
> &
  Pick<TextFieldProps, 'placeholder' | 'helperText' | 'disabled'>;

const BasicSelectInner = <
  T extends string | number | Array<string | number> | null = string,
>(
  {
    value,
    onChange,
    onClose,
    label,
    id: providedId,
    options,
    fullWidth,
    multiple,
    placeholder,
    helperText,
    error,
    disabled,
    className,
    noOptionsMessage = 'No options available',
    nullOptionLabel,
    renderValue,
  }: BasicSelectBaseProps<T>,
  ref: Ref<HTMLSelectElement>,
): JSX.Element => {
  const generatedId = useId();
  const id = providedId ?? generatedId;

  return (
    <FormControl fullWidth={fullWidth} className={className}>
      {!!label && <InputLabel id={id}>{label}</InputLabel>}
      <StyledSelect
        ref={ref}
        id={id}
        multiple={multiple}
        labelId={label ? id : undefined}
        value={value ?? ''}
        label={label}
        onChange={(e) => {
          onChange(e.target.value as T);
        }}
        onClose={() => {
          onClose?.();
        }}
        displayEmpty={Boolean(placeholder)}
        error={error}
        variant="outlined"
        renderValue={(selected) => {
          if (!selected && placeholder) {
            return <Placeholder variant="inherit">{placeholder}</Placeholder>;
          }
          if (renderValue) {
            return renderValue(selected as T);
          }
          if (isArray(selected)) {
            return options
              .filter((option) => selected.includes(option.value))
              .map((option) => option.label)
              .join(', ');
          } else {
            return (
              options.find((option) => option.value === selected)?.label ??
              placeholder
            );
          }
        }}
        MenuProps={{
          disablePortal: true,
          slots: {
            backdrop: InvisibleBackdrop,
          },
        }}
        inputProps={{
          placeholder,
          disabled,
        }}
        slotProps={{
          input: {
            placeholder,
          },
        }}
        disabled={disabled}
      >
        {nullOptionLabel ? (
          <Option value={undefined}>{nullOptionLabel}</Option>
        ) : null}
        {options.length ? (
          options.map((option) => (
            <Option
              key={option.value}
              value={option.value ?? undefined}
              disabled={option.disabled}
            >
              {option.description ? (
                <Column fullWidth>
                  <div>{option.label}</div>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </Column>
              ) : (
                option.label
              )}
              {value &&
              (isArray(value) && option.value
                ? value.includes(option.value)
                : option.value === value) ? (
                <Check fontSize="small" color="action" />
              ) : null}
            </Option>
          ))
        ) : (
          <Option disabled value="empty">
            {noOptionsMessage}
          </Option>
        )}
      </StyledSelect>
      {!!helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

const BasicSelect = forwardRef(BasicSelectInner) as unknown as {
  <T extends string | number | Array<string | number> | null = string>(
    props: BasicSelectBaseProps<T> & { ref?: Ref<HTMLSelectElement> },
  ): JSX.Element;
  displayName?: string;
  nullOptionLabel?: string;
};

BasicSelect.displayName = 'BasicSelect';

export default BasicSelect;
